import {
  AppBar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  Toolbar,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { RouteNames } from "../../navigation/RouteNames";
import { useTranslation } from "../../translations/i18n";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { logoutUser } from "../../store/slices/authSlice";
import { AppColors } from "../../constants/AppColors";
import {
  canManageBilling,
  canManageOperations,
  canManageTeamMembers,
  canViewCalendar,
  canViewCleaningTasks,
  canViewInvoices,
  getRoleTranslationKey,
} from "../../utils/helperFunctions/roleHelper";
import EmailVerificationBanner from "../auth/EmailVerificationBanner";
import SubscriptionAccessBanner from "../auth/SubscriptionAccessBanner";
import SubscriptionAccessModal from "../auth/SubscriptionAccessModal";
import { isCustomSubscription } from "../../utils/helperFunctions/subscriptionAccessHelper";

const drawerWidth = 280;

type MenuItem = {
  label: string;
  path: string;
};

export const AppLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { user, account } = useAppSelector((state) => state.auth);

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const menu = useMemo(() => {
    const items: MenuItem[] = [];

    if (canViewCalendar(user?.role)) {
      items.push({ label: t("tabs.calendar"), path: RouteNames.calendar });
    }

    if (canViewCleaningTasks(user?.role)) {
      items.push({
        label: t("tabs.cleaningTasks"),
        path: RouteNames.cleaningTasks,
      });
    }

    if (canManageOperations(user?.role)) {
      items.push(
        { label: t("tabs.reservations"), path: RouteNames.reservations },
        { label: t("tabs.guests"), path: RouteNames.guests },
        { label: t("tabs.properties"), path: RouteNames.properties },
        { label: t("tabs.statistics"), path: RouteNames.statistics },
      );
    }

    if (canManageTeamMembers(user?.role)) {
      items.push({ label: t("tabs.team"), path: RouteNames.team });
    }

    if (canViewInvoices(user?.role)) {
      items.push({ label: t("tabs.invoices"), path: RouteNames.invoices });
    }

    if (canManageBilling(user?.role) && !isCustomSubscription(account)) {
      items.push({
        label: t("subscription.title"),
        path: RouteNames.subscription,
      });
    }

    items.push({ label: t("tabs.menu"), path: RouteNames.menu });

    return items;
  }, [account, t, user?.role]);

  const active = (path: string) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(path);

  const handleNavigate = (path: string) => {
    navigate(path);
    setMobileDrawerOpen(false);
  };

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate(RouteNames.login, { replace: true });
  };

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: AppColors.surface,
      }}
    >
      <Toolbar
        sx={{
          px: 3,
          py: 2,
          minHeight: 80,
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "16px",
            bgcolor: AppColors.primary,
            color: AppColors.textOnPrimary,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 900,
            fontSize: 20,
          }}
        >
          RP
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1 }}>
            RentPilot
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Booking management
          </Typography>
        </Box>

        <IconButton
          onClick={() => setMobileDrawerOpen(false)}
          sx={{ display: { xs: "inline-flex", md: "none" } }}
        >
          <CloseIcon />
        </IconButton>
      </Toolbar>

      <Divider />

      <Box sx={{ p: 2 }}>
        <Typography sx={{ fontWeight: 900 }}>
          {user?.name || user?.first_name || "User"}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          {user?.email}
        </Typography>

        <Chip
          sx={{ mt: 1, textTransform: "capitalize", mr: 1 }}
          size="small"
          color="primary"
          label={t(getRoleTranslationKey(user?.role))}
        />

        {canManageBilling(user?.role) ? (
          <Chip
            sx={{ mt: 1, textTransform: "capitalize" }}
            size="small"
            color={
              account?.effective_subscription_plan || account?.subscription_plan
                ? "primary"
                : "default"
            }
            label={`${
              account?.effective_subscription_plan ||
              account?.subscription_plan ||
              "basic"
            } plan`}
          />
        ) : null}
      </Box>

      <Divider />

      <List sx={{ px: 1.5, py: 2, flex: 1 }}>
        {menu.map((item) => (
          <ListItemButton
            key={item.path}
            selected={active(item.path)}
            onClick={() => handleNavigate(item.path)}
            sx={{ borderRadius: 2, mb: 0.5 }}
          >
            <Typography
              sx={{
                fontWeight: active(item.path) ? 900 : 650,
                fontSize: 15,
              }}
            >
              {item.label}
            </Typography>
          </ListItemButton>
        ))}
      </List>

      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          color="inherit"
          onClick={handleLogout}
        >
          {t("auth.logout")}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: AppColors.background,
      }}
    >
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          display: { xs: "block", md: "none" },
          bgcolor: AppColors.surface,
          color: AppColors.textPrimary,
          borderBottom: `1px solid ${AppColors.border}`,
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            onClick={() => setMobileDrawerOpen(true)}
            sx={{ mr: 1 }}
          >
            <MenuIcon />
          </IconButton>

          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "12px",
              bgcolor: AppColors.primary,
              color: AppColors.textOnPrimary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 900,
              mr: 1,
            }}
          >
            RP
          </Box>

          <Typography sx={{ fontWeight: 900 }}>RentPilot</Typography>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            borderRightColor: AppColors.border,
            bgcolor: AppColors.surface,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Drawer
        variant="temporary"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { xs: "100%", md: `calc(100% - ${drawerWidth}px)` },
          p: { xs: 2, sm: 3, md: 4 },
          pt: { xs: 10, md: 4 },
          overflowX: "hidden",
        }}
      >
        <EmailVerificationBanner />
        <SubscriptionAccessBanner />
        <Outlet />
        <SubscriptionAccessModal />
      </Box>
    </Box>
  );
};
